import type { PdfRouter } from '@trpc-endpoints/trpc-pdf/router';
import { createTRPCProxyClient, httpLink, OperationLink, TRPCClientError } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { trpcFetcher } from 'reusable/fetching/trpc-fetcher';
import variables from 'variables/variables';
import superjson from 'superjson';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth'
import { AnyRouter } from '@trpc/server';

const createClient = (silent?: boolean) => createTRPCProxyClient<PdfRouter>({
   transformer: superjson,
   links: [
      errorTransformerLink,
      httpLink({
         url: `${variables.apiUrl}/trpc-pdf`,
         async fetch(input: RequestInfo | URL, options?: RequestInit): Promise<Response> {
            let token: string | null = null
            const session: AuthSession = await fetchAuthSession()

            // check to see if we have a token issued using amplify
            if (!session.tokens?.accessToken) {
               // if not, then check our gap issued tokens
               token = localStorage.getItem(variables.gapAccessTokenLocalStorageKey)
               if (!token) {
                  throw new Error('User has no access token.')
               }
            } else {
               token = session.tokens.accessToken.toString()
            }

            return trpcFetcher({
               silent,
               fetchParams: [input, options],
               cors: true,
               auth: { type: 'jwt', token },
            })
         },
      }),
   ],
});

export function errorTransformerLink<TRouter extends AnyRouter>(): OperationLink<TRouter> {
   return (opts) => {
      return observable((observer) => {
         const unsubscribe = opts.next(opts.op).subscribe({
            error(error: TRPCClientError<TRouter>) {
               if (error.shape?.cause) {
                  const cause = new Error()
                  Object.assign(cause, error.shape.cause);
                  (error as Error).cause = cause
               }
               observer.error(error);
            },
            next: observer.next,
            complete: observer.complete,
         });

         return unsubscribe;
      })
   }
}

export const pdfClient = createClient()
export const pdfClientSilent = createClient(true)